import React, { useRef, useState, useMemo, useEffect } from "react";
import { makeStyles, lighten, alpha } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import clsx from "clsx";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import Alert from "@material-ui/lab/Alert";

import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import { formatDollars } from "src/utils";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { createTableToolbar } from "src/components/TableToolbar";
import { Columns } from "src/pages/TransactionDetails/All";
import { Columns as SalesColumns } from "src/pages/TransactionDetails/Sales/columns";

import {
  type InventoryReport as InventoryReportType,
  type InventoryReportData,
} from "./report";
import CompanyInfo from "./CompanyInfo";
import Title, { DownloadButton } from "./Title";
import { render as pdfRender } from "./pdf";
import Details from "./Details";
import { useOpenDialog } from "./useOpenDialog";
import StaleReportWarning from "./StaleReportWarning";

interface InventoryReportProps {
  results: InventoryReportType;
  showStaleReportWarning?: boolean;
  onRerunReport?: () => void;
  onReportDataMutated?: () => void;
}

const useStyles = makeStyles((theme) => ({
  majorRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(1, 0, 0.5, 0),
    padding: theme.spacing(0, 1),
    width: 420,
  },
  rowLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    width: 310,
  },
  sign: {
    fontWeight: theme.typography.fontWeightMedium,
    width: 20,
  },
  value: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  resultsContainer: {
    margin: "0 auto",
    width: 480,
  },
  selected: {
    background: alpha(theme.palette.primary.main, 0.15),
    borderRadius: `${theme.shape.borderRadius}px`,
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.25),
    },
  },
  alert: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: lighten(theme.palette.warning.main, 0.8),
    },
  },
}));

function getNameByKey(selected: keyof InventoryReportData): string {
  return selected === "invalidPurchaseDateSales"
    ? "Inventory Purchase Date after Sale Date"
    : selected === "beginning"
    ? "Inventory Balance at Beginning of Year"
    : selected === "during"
    ? "Inventory Purchases During the Year"
    : selected === "end"
    ? "Inventory Balance at End of Year"
    : "Cost of Goods Sold";
}

function InventoryReport({
  results,
  showStaleReportWarning,
  onRerunReport,
  onReportDataMutated,
}: InventoryReportProps) {
  const year = results.year;
  const dispatch = useDispatch();
  const openDialog = useOpenDialog();
  const [selected, setSelected] = useState<keyof InventoryReportData>();
  const report = results.data;
  const classes = useStyles();
  const ref = useRef<HTMLElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  // Scroll to details when it opens
  useEffect(() => {
    if (selected && detailsRef.current) {
      const timeout = setTimeout(() => {
        detailsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [selected]);
  const handleDownloadReport = async () => {
    if (!ref?.current) return;
    await pdfRender(ref.current, {
      filename: `Inventory Report - ${year}`,
      onclone: (doc) => {
        const toolbar = doc.querySelector(".MuiDataGrid-root > div");
        if (toolbar) toolbar.remove();
      },
    });
  };
  const toolbar = useMemo(() => {
    if (!selected) return undefined;
    const name = getNameByKey(selected);
    return createTableToolbar({
      options: {
        fileName: `Inventory Report - ${year} - ${name}`,
      },
    });
  }, [selected, year]);
  const showWarning =
    (
      report.beginning.amount +
      report.during.amount -
      report.end.amount
    ).toFixed(2) !== report.cost.amount.toFixed(2);

  return (
    <Box component={Paper} p={2} mt={2} width="100%" {...({ ref } as any)}>
      {showStaleReportWarning ? (
        <StaleReportWarning onRerunClick={onRerunReport} />
      ) : null}
      <CompanyInfo />
      <Title actions={<DownloadButton onClick={handleDownloadReport} />}>
        Inventory Report for {year}
      </Title>
      <div className={classes.resultsContainer}>
        <Box display="flex" justifyContent="space-between">
          <Tooltip title="Click to view details">
            <div
              className={clsx(
                classes.majorRow,
                selected === "beginning" && classes.selected
              )}
              onClick={() =>
                setSelected((s) =>
                  s === "beginning" ? undefined : "beginning"
                )
              }
            >
              <div className={classes.rowLabel}>
                Inventory Balance at Beginning of Year
              </div>
              <div className={classes.sign} />
              <div className={classes.value}>
                {formatDollars(report.beginning.amount)}
              </div>
            </div>
          </Tooltip>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Tooltip title="Click to view details">
            <div
              className={clsx(
                classes.majorRow,
                selected === "during" && classes.selected
              )}
              onClick={() =>
                setSelected((s) => (s === "during" ? undefined : "during"))
              }
            >
              <div className={classes.rowLabel}>
                Inventory Purchases During the Year
              </div>
              <div className={classes.sign}>+</div>
              <div className={classes.value}>
                {formatDollars(report.during.amount)}
              </div>
            </div>
          </Tooltip>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Tooltip title="Click to view details">
            <div
              className={clsx(
                classes.majorRow,
                selected === "end" && classes.selected
              )}
              onClick={() =>
                setSelected((s) => (s === "end" ? undefined : "end"))
              }
            >
              <div className={classes.rowLabel}>
                Inventory Balance at End of Year
              </div>
              <div className={classes.sign}>-</div>
              <div className={classes.value}>
                {formatDollars(report.end.amount)}
              </div>
            </div>
          </Tooltip>
        </Box>
        <Divider />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Tooltip title="Click to view details">
            <div
              className={clsx(
                classes.majorRow,
                selected === "cost" && classes.selected
              )}
              onClick={() =>
                setSelected((s) => (s === "cost" ? undefined : "cost"))
              }
            >
              <div className={classes.rowLabel}>Cost of Goods Sold</div>
              <div className={classes.sign} />
              <div className={classes.value}>
                {formatDollars(report.cost.amount)}
              </div>
            </div>
          </Tooltip>
        </Box>
        {showWarning ? (
          <Box mt={2}>
            <Alert
              className={classes.alert}
              severity="warning"
              onClick={() => setSelected("invalidPurchaseDateSales")}
            >
              You have some sales with a purchase date after the sale date.
              Click here to view and correct them.
            </Alert>
          </Box>
        ) : null}
      </div>
      <div ref={detailsRef}>
        <Details
          open={!!selected}
          onClose={() => setSelected(undefined)}
          title={selected ? getNameByKey(selected) : undefined}
        >
          {selected === "invalidPurchaseDateSales" ? (
            <XGrid
              Component={CSVFormattedXGrid}
              components={{
                Toolbar: toolbar,
              }}
              rows={report.invalidPurchaseDateSales || []}
              columns={SalesColumns}
              headerHeight={35}
              rowHeight={35}
              onCellDoubleClick={async (ev) => {
                const res = await openDialog("sale", ev.row.id);
                if (res.status === "updated") onReportDataMutated?.();
              }}
            />
          ) : selected ? (
            <XGrid
              Component={CSVFormattedXGrid}
              components={{
                Toolbar: toolbar,
              }}
              rows={report[selected].transactions || []}
              columns={Columns}
              headerHeight={35}
              rowHeight={35}
              onCellDoubleClick={async (ev) => {
                if (ev.row?.journal_entry_id) {
                  dispatch(
                    setActiveDialog("add_journal", {
                      id: ev.row.journal_entry_id,
                    })
                  );
                  return;
                }
                if (ev.row.collection === "transaction") {
                  const res = await openDialog(
                    "transaction",
                    ev.row.collection_id
                  );
                  if (res.status === "updated") onReportDataMutated?.();
                  return;
                }

                if (ev.row.collection === "inventory") {
                  const res = await openDialog(
                    "inventory",
                    ev.row.collection_id
                  );
                  if (res.status === "updated") onReportDataMutated?.();
                  return;
                }

                if (ev.row.collection === "sale") {
                  const res = await openDialog("sale", ev.row.collection_id);
                  if (res.status === "updated") onReportDataMutated?.();
                  return;
                }
              }}
            />
          ) : null}
        </Details>
      </div>
    </Box>
  );
}

export default InventoryReport;
